import { CheckCircleTwoTone } from '@ant-design/icons';
import { Button, Flex, Typography } from 'antd';

import type {
  ColumnDataType,
  GetSalaryTableConfigProps,
} from '@app/pages/Analytics/SalaryReport/types';
import { divideAmount } from '@app/utils';

import { READ_ONLY_SALARY_FIELDS } from './readOnlySalaryFields';

import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

export const getSalaryTableConfig = ({
  tableData,
  onConfirm,
  handleEditableCellClick,
  handleRevokeConfirm,
  hasUserRightToConfirmSalary,
  hasUserRightToEditBefore,
  hasUserRightToEditAfterConfirm,
}: GetSalaryTableConfigProps) => {
  const columns: ColumnsType<ColumnDataType> = [
    {
      title: '№ п/п',
      dataIndex: 'Index',
      key: 'Index',
      width: 60,
    },
    {
      title: 'Пользователь',
      dataIndex: 'UserFullName',
      key: 'UserFullName',
      width: 150,
      render: (text, record) => {
        const { IsDeleted } = record;
        return (
          <Text>
            {text}
            <br />
            {IsDeleted && <Text type="danger">В архиве</Text>}
          </Text>
        );
      },
    },
    {
      title: 'Должность',
      dataIndex: 'Position',
      key: 'Position',
      width: 150,
    },
    {
      title: 'Заработная плата',
      children: [
        'TotalAmount',
        'AdvanceAmount',
        'BonusAmount',
        'OfficialAmount',
      ].map((field) => ({
        title: {
          TotalAmount: 'Общая',
          AdvanceAmount: 'Аванс',
          BonusAmount: 'Премия',
          OfficialAmount: 'Официальная',
        }[field],
        dataIndex: field,
        key: field,
        align: 'right' as const,
        render: (text, record) => {
          if (READ_ONLY_SALARY_FIELDS.has(field)) {
            return <Text>{divideAmount(text)}</Text>;
          }

          return (
            <div
              className={
                hasUserRightToEditBefore || hasUserRightToEditAfterConfirm
                  ? 'editable-cell'
                  : ''
              }
              onClick={() => handleEditableCellClick(record, field)}
            >
              <Text>{divideAmount(text)}</Text>
            </div>
          );
        },
      })),
    },
    {
      title: 'Получение',
      key: 'IsConfirmed',
      width: 200,
      render: (_, record) =>
        record.IsConfirmed ? (
          <Flex gap={16} vertical>
            <Text type="success" className="confirmed-text">
              <CheckCircleTwoTone twoToneColor="#52c41a" /> Подтверждено
            </Text>
            {hasUserRightToConfirmSalary && (
              <Button danger onClick={() => handleRevokeConfirm(record.UserId)}>
                Отменить
              </Button>
            )}
          </Flex>
        ) : (
          hasUserRightToConfirmSalary && (
            <Button
              size="small"
              type="primary"
              ghost
              onClick={() => onConfirm(record.UserId)}
            >
              Подтвердить
            </Button>
          )
        ),
    },
    {
      title: 'Комментарий',
      dataIndex: 'Comment',
      key: 'Comment',
      width: 150,
      render: (text, record) => {
        return (
          <div
            className={
              hasUserRightToEditBefore || hasUserRightToEditAfterConfirm
                ? 'editable-cell'
                : ''
            }
            onClick={() => handleEditableCellClick(record, 'Comment')}
          >
            <Text>{text}</Text>
          </div>
        );
      },
    },
  ];

  const dataSource: ColumnDataType[] =
    tableData?.Salaries?.map((row, idx) => {
      const {
        UserId,
        TotalAmount,
        AdvanceAmount,
        BonusAmount,
        OfficialAmount,
        TimeConfirmed,
        Comment,
        UserFullName,
        PositionName,
        IsDeleted,
      } = row;

      const rowKey = `${PositionName}-${UserId}-${idx}`;

      return {
        key: rowKey,
        Key: rowKey,
        Index: idx + 1,
        UserFullName: UserFullName ?? '',
        Position: PositionName ?? '',
        TotalAmount,
        AdvanceAmount,
        BonusAmount,
        OfficialAmount,
        IsConfirmed: Boolean(TimeConfirmed),
        Comment: Comment ?? '',
        UserId,
        IsDeleted,
      };
    }) || [];

  return { columns, dataSource };
};
